import {FormControl, FormField, Tooltip} from 'platform/components';
import {Grid, GridItem, Heading, VStack} from 'platform/foundation';

import {not} from 'ramda';

import {
  GetSupplierDiscountGroupsResponse,
  GetWarehouseHandlingUnitsResponse,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {getOptionsFromHandlingUnits} from '@dms/shared-aftersales';
import {getOptionsFromSupplierDiscountGroups} from '@dms/warehouse/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {ReceiveNoteItemFormType} from '../schemas/ReceiveNoteItemDetailSchema';

type LoadingState = {
  isLoadingHandlingUnits: boolean;
  isLoadingSupplierDiscountGroups: boolean;
};

export interface SupplierArticleFormProps extends RequiredTestIdProps {
  control: FormControl<ReceiveNoteItemFormType>;
  handlingUnits: GetWarehouseHandlingUnitsResponse | Nullish;
  isChecked: boolean;
  isEditingDisabled: boolean;
  isOpeningBalanceEditingDisabled: boolean;
  isTransferEditingDisabled: boolean;
  supplierDiscountGroups: GetSupplierDiscountGroupsResponse | Nullish;
  loadingState: LoadingState;
}

export function SupplierArticleForm(props: SupplierArticleFormProps) {
  const isFieldDisabled =
    not(props.isChecked) ||
    props.isEditingDisabled ||
    props.isOpeningBalanceEditingDisabled ||
    props.isTransferEditingDisabled;

  const getEditableFieldTooltipLabel = () => {
    if (not(props.isChecked)) {
      return i18n.t('entity.warehouse.tooltips.fieldDisabledItemNotChecked');
    }
    if (props.isEditingDisabled) {
      return i18n.t('entity.warehouse.tooltips.fieldDisabledReceiveNoteCompleted');
    }
    if (props.isOpeningBalanceEditingDisabled) {
      return i18n.t('entity.warehouse.tooltips.fieldDisabledOpeningBalance');
    }
    if (props.isTransferEditingDisabled) {
      return i18n.t('entity.warehouse.tooltips.fieldDisabledInTransfer');
    }
    return '';
  };

  return (
    <VStack spacing={4} data-testid={props['data-testid']}>
      <Heading size={5}>{i18n.t('entity.warehouse.labels.supplierArticle')}</Heading>

      <Grid columns={4}>
        <GridItem span={1}>
          <Tooltip label={getEditableFieldTooltipLabel()} isDisabled={!isFieldDisabled}>
            <FormField
              control={props.control}
              isDisabled={isFieldDisabled}
              type="number"
              name="supplierArticle.supplierQuantity"
              label={i18n.t('entity.warehouse.labels.supplierQuantity')}
              decimalPlaces={4}
              data-testid={suffixTestId('inputs.supplierQuantity', props)}
            />
          </Tooltip>
        </GridItem>
        <GridItem span={1}>
          <Tooltip label={getEditableFieldTooltipLabel()} isDisabled={!isFieldDisabled}>
            <FormField
              control={props.control}
              isRequired
              isNotClearable
              isLoading={props.loadingState.isLoadingHandlingUnits}
              isDisabled={isFieldDisabled}
              type="choice"
              name="supplierArticle.supplierUnitId"
              label={i18n.t('entity.warehouse.labels.supplierUnit')}
              options={getOptionsFromHandlingUnits(props.handlingUnits)}
              data-testid={suffixTestId('inputs.supplierUnit', props)}
            />
          </Tooltip>
        </GridItem>
        <GridItem span={1}>
          <Tooltip label={getEditableFieldTooltipLabel()} isDisabled={!isFieldDisabled}>
            <FormField
              control={props.control}
              isDisabled={isFieldDisabled}
              type="number"
              name="supplierArticle.warehouseQuantity"
              label={i18n.t('entity.warehouse.labels.warehouseQuantity')}
              decimalPlaces={4}
              data-testid={suffixTestId('inputs.warehouseQuantity', props)}
            />
          </Tooltip>
        </GridItem>
        <GridItem span={1}>
          <Tooltip label={i18n.t('entity.warehouse.tooltips.disabled')}>
            <FormField
              control={props.control}
              isLoading={props.loadingState.isLoadingHandlingUnits}
              isDisabled
              isNotClearable
              type="choice"
              name="supplierArticle.warehouseUnitId"
              label={i18n.t('entity.warehouse.labels.warehouseUnit')}
              options={getOptionsFromHandlingUnits(props.handlingUnits)}
              menuInPortal
              data-testid={suffixTestId('inputs.warehouseUnit', props)}
            />
          </Tooltip>
        </GridItem>
        <GridItem span={1}>
          <Tooltip label={getEditableFieldTooltipLabel()} isDisabled={!isFieldDisabled}>
            <FormField
              control={props.control}
              isDisabled={isFieldDisabled}
              type="text"
              name="supplierArticle.supplierOrderingNumber"
              label={i18n.t('entity.warehouse.labels.supplierOrderingNumber')}
              data-testid={suffixTestId('inputs.supplierOrderingNumber', props)}
            />
          </Tooltip>
        </GridItem>
        <GridItem span={1}>
          <Tooltip label={getEditableFieldTooltipLabel()} isDisabled={!isFieldDisabled}>
            <FormField
              control={props.control}
              isDisabled={isFieldDisabled}
              type="number"
              name="supplierArticle.supplierBulkPackageQuantity"
              label={i18n.t('entity.warehouse.labels.supplierBulkPackageQuantity')}
              decimalPlaces={4}
              data-testid={suffixTestId('inputs.supplierBulkPackageQuantity', props)}
            />
          </Tooltip>
        </GridItem>
        <GridItem span={1}>
          <Tooltip label={i18n.t('entity.warehouse.tooltips.disabled')}>
            <FormField
              control={props.control}
              isDisabled
              type="text"
              name="supplierArticle.supplier"
              label={i18n.t('entity.warehouse.labels.supplier')}
              data-testid={suffixTestId('inputs.supplier', props)}
            />
          </Tooltip>
        </GridItem>
        <GridItem span={1}>
          <Tooltip label={i18n.t('entity.warehouse.tooltips.disabled')}>
            <FormField
              control={props.control}
              isDisabled
              isLoading={props.loadingState.isLoadingSupplierDiscountGroups}
              type="choice"
              name="supplierArticle.supplierDiscountGroup"
              label={i18n.t('entity.warehouse.labels.supplierDiscountGroup')}
              options={getOptionsFromSupplierDiscountGroups(props.supplierDiscountGroups)}
              data-testid={suffixTestId('inputs.supplierDiscountGroup', props)}
            />
          </Tooltip>
        </GridItem>
      </Grid>
    </VStack>
  );
}
